import { useState, useEffect, useRef ,  useMemo  } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Avatar,
    Chip,
    Divider,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    Paper,
    Snackbar,
    Alert,
    Typography,
    Checkbox,
    InputAdornment,
    TextField,
    LinearProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import LeadSourceMaster from './Test/components/LeadSourceMaster';
import { useAuth } from '../context/AuthContext';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import { useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// ── SourceModule ──────────────────────────────────────────────────────────────
// Lists lead sources (e.g., "Website", "Referral", "Cold Call") for the company.
// Used in the Inquiry module to track where a lead came from.
// Each source can be flagged as the default when creating a new inquiry.

// Converts any string to a consistent color for avatar backgrounds
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4'];
    return colors[Math.abs(hash) % colors.length];
}

// Returns the first letter of a name in uppercase (used for avatar initials)
function getInitials(name) {
    if (!name) return 'S';
    return name.trim().charAt(0).toUpperCase();
}

const SourceModule = ({ companyId, connectionString, mode, setMode }) => {

    const API = `${connectionString}/api`;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    // const [mode, setMode] = useState('comp');
    const [selectionModel, setSelectionModel] = useState([]);
    const selectedIdsRef = useRef([]);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalCount, setTotalCount] = useState(0);
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [selectAll, setSelectAll] = useState(false);
const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
        const { user } = useAuth();
        const role = user?.UserCategory;


        const filteredData = useMemo(() => {
    if (!isMobile) return data;        // 🖥 desktop untouched

    if (!mobileSearchQuery.trim()) return data;

    const q = mobileSearchQuery.toLowerCase();

    return data.filter(row =>
        String(row.LeadSource || '').toLowerCase().includes(q)
    );
}, [data, mobileSearchQuery, isMobile]);

const toggleSelectAll = () => {
    if (selectAll) {
        setSelectionModel([]);
        selectedIdsRef.current = [];
        setSelectAll(false);
    } else {
        const source = isMobile ? filteredData : data;
        const allIds = source.map(item => item.id);

        setSelectionModel(allIds);
        selectedIdsRef.current = allIds;
        setSelectAll(true);
    }
};


    const fetchData = async () => {
        setLoading(true);
        try {
            const { data: response } = await axios.get(
                `${API}/source/get-source`,
                { params: { CompId: companyId } }
            );

            const rows = response?.data || [];

            const transformedData = rows.map((item, index) => ({
                ...item,
                id: Number(item.LeadsourceID || item.SourceId) || index
            }));

            setData(transformedData);
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to fetch source data',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (companyId) fetchData();
    }, [companyId]);

    const handleAdd = () => {
        setMode('comp');
        setEditingId(null);
        setOpenDialog(true);
    };

    const handleEdit = (row) => {
        setMode('edit');
        setEditingId(row.id);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingId(null);
        fetchData();
    };

    // const handleDelete = async (id) => {
    //     if (!window.confirm('Are you sure you want to delete this source?')) return;

    //     try {
    //         await axios.post(`${API}/source/delete`, {
    //             objAryId: [id]
    //         });
    //         setSnackbar({ open: true, message: 'Source deleted successfully', severity: 'success' });
    //         fetchData();
    //     } catch {
    //         setSnackbar({ open: true, message: 'Failed to delete source', severity: 'error' });
    //     }
    // };

    const handleDelete = async (ids) => {
        const numericIds = ids.map(Number);
        if (!numericIds.length || deleting) return;

        // if (!window.confirm(`Are you sure you want to delete ${numericIds.length} tax record(s)?`))
        //     return;
        setDeleting(true);

        try {
            // await axios.post(`${API}/source/delete`, {
            //     objAryId: numericIds,
            //     CompId: companyId
            // });

            // setSnackbar({
            //     open: true,
            //     message: 'Deleted Successfully.',
            //     severity: 'success'
            // });

            const response = await axios.post(`${API}/source/delete`, {
                objAryId: numericIds,
                CompId: companyId
            });

            const { deletedCount, skippedCount } = response.data;

            if (deletedCount > 0) {
                setSnackbar({
                    open: true,
                    message: "Deleted Successfully",
                    severity: "success"
                });
            }

            setSelectionModel([]);
            selectedIdsRef.current = [];
            fetchData();
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            // 🔴 Case: 400 + referenced IDs (business rule failure)
            if (status === 400 && data?.referencedIds?.length > 0) {
                setSnackbar({
                    open: true,
                    message: "Cannot delete the selected item(s) because they are referenced in other records.",
                    severity: "warning"
                });

            } else if (status === 400 && data?.message) {
                // 🔴 400 but no referencedIds array (generic validation)
                setSnackbar({
                    open: true,
                    message: data.message,
                    severity: "warning"
                });
            } else {
                // 🔴 unexpected / server error
                setSnackbar({
                    open: true,
                    message: "Delete failed due to an unexpected error.",
                    severity: "error"
                });
            }
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };


    const columns = [
        {
            field: 'LeadSource',
            headerName: 'Source Name',
            flex: 1,
            renderCell: (params) => (
                <Typography
                    sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'underline'
                    }}
                    onClick={(e) => {
                        e.stopPropagation(); // prevent row selection
                        handleEdit(params.row);
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'IsDefault',
            headerName: 'Default',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            filterable: false,
            renderCell: (params) => (
                params.value === 1 ? 'Yes' : '-'
            )
        },
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 120,
        //     sortable: false,
        //     filterable: false,
        //     renderCell: (params) => (
        //         <Box>
        //             <Tooltip title="Edit">
        //                 <IconButton onClick={() => handleEdit(params.row)}>
        //                     <EditIcon fontSize="small" />
        //                 </IconButton>
        //             </Tooltip>
        //             <Tooltip title="Delete">
        //                 <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
        //                     <DeleteIcon fontSize="small" />
        //                 </IconButton>
        //             </Tooltip>
        //         </Box>
        //     )
        // }
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isRowSelected = selectedIdsRef.current.includes(params.row.id);

                return (
                    <Box>
                        {/* DELETE – enabled only when row is selected */}
                        <Tooltip
                            title={
                                isRowSelected
                                    ? "Delete selected"
                                    : "Select this row to enable delete"
                            }
                        >
                            {/* span required for disabled tooltip */}
                            <span>
                                <IconButton
                                    color="error"
                                    disabled={!isRowSelected}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isRowSelected) return;

                                        const idsToDelete =
                                            selectedIdsRef.current.length > 0
                                                ? selectedIdsRef.current
                                                : [params.row.id];

                                        setPendingDeleteIds(idsToDelete);
                                        setConfirmOpen(true);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            }
        }

    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

      const renderMobileCards = () => {
            if (loading) {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                );
            }
    
            if (filteredData.length === 0) {
                return <CustomNoDataOverlay />;
            }
    
            return (
                <>
                    {/* Each source rendered as a gradient card matching the app-wide card style */}
                    {filteredData.map((row) => {
                        const isSelected = selectionModel.includes(row.id);
                        const avatarColor = stringToColor(row.LeadSource || 'S');
                        const initials = getInitials(row.LeadSource);

                        return (
                            <Card key={row.id} sx={{ mb: 1.5, borderRadius: 2, boxShadow: 1, overflow: 'hidden', transition: '0.2s', '&:hover': { boxShadow: 3 } }}>
                                {/* Color strip at top — unique color per source name */}
                                <Box sx={{ height: 5, background: `linear-gradient(90deg, ${avatarColor} 0%, ${avatarColor}88 100%)` }} />
                                <CardContent sx={{ py: 1.5, px: 1.5 }}>
                                    {/* Header row: optional checkbox + avatar initial + source name */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                        {role !== 'User' && (
                                            <Checkbox size="small" checked={isSelected} onChange={() => {
                                                const updated = isSelected
                                                    ? selectionModel.filter(id => id !== row.id)
                                                    : [...selectionModel, row.id];
                                                setSelectionModel(updated);
                                                selectedIdsRef.current = updated;
                                            }} sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }} />
                                        )}
                                        <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 38, height: 38, fontWeight: 700, mr: 1.5, flexShrink: 0, fontSize: '1rem' }}>
                                            {initials}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleEdit(row)}>
                                                {row.LeadSource}
                                            </Typography>
                                        </Box>
                                        {/* "Default" badge shown if this source is the default */}
                                        {row.IsDefault !== 0 && (
                                            <Chip label="Default" color="success" size="small" sx={{ height: 20, fontSize: '0.7rem', ml: 1 }} />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
    
                    {/* Pagination */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            py: 2
                        }}
                    >
                        <Button variant="outlined" size="small" startIcon={<NavigateBeforeIcon />}
                            disabled={paginationModel.page === 0}
                            onClick={() => setPaginationModel((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}>
                            Previous
                        </Button>
                        <Typography variant="body2">
                            Page {paginationModel.page + 1} of{' '}
                            {Math.ceil(totalCount / paginationModel.pageSize) || 1}
                        </Typography>
                        <Button variant="outlined" size="small" endIcon={<NavigateNextIcon />}
                            disabled={(paginationModel.page + 1) * paginationModel.pageSize >= totalCount}
                            onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}>
                            Next
                        </Button>
                    </Box>
                </>
            );
        };

    return (
        <Box>
            {/* ── Module Header: gradient strip + title + Add button ─────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Lead Sources
                    </Typography>
                    <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAdd}>
                        Add Source
                    </Button>
                </Paper>
            </Box>

      
                {data.length === 0 ? <NoDataComponent /> :
                
                  isMobile ? (
                                    <>
                                  
                                        <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                                            <TextField fullWidth size="small" placeholder="Search Source..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)} variant="outlined"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                        {role !== 'User' && selectionModel.length > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }} >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Checkbox checked={selectAll} onChange={toggleSelectAll} size="small" />
                                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                                        {selectionModel.length} selected
                                                    </Typography>
                                                </Box>
                                                <Tooltip title="Delete selected">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                
                                                            // ✅ pass selected ids
                                                            setPendingDeleteIds(selectionModel);
                                                            setConfirmOpen(true);
                                                        }}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                
                                                </Tooltip>
                                            </Box>
                                        )}
                
                                        <Box>{renderMobileCards()}</Box>
                                    </>
                                ) :
                                      <Box sx={{ height: 400 }}>
                    <DataGrid
                        rows={data}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 25]}
                        pagination

                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(newSelection) => {
                            const numeric = newSelection.map(Number);
                            setSelectionModel(numeric);
                            selectedIdsRef.current = numeric;
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                            // Background styling
                            '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                            // Row styling
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                            // Selected row styling (with !important to override default MUI)
                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                            // Cell content alignment
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center'
                            },
                        }}
                        slots={{
                            noResultsOverlay: CustomNoDataOverlay
                        }}
                        
                    />
                        </Box>
                    }
        

            <Dialog open={openDialog} maxWidth="sm" fullWidth>
                <LeadSourceMaster
                    mode={mode}                 // ✅ controlled
                    id={editingId}
                    companyId={companyId}       // ✅ ADD THIS
                    connectionString={connectionString}
                    onPopupCancel={handleClose}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => { setSnackbar(prev => ({ ...prev, open: false })) }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => { setSnackbar(prev => ({ ...prev, open: false })) }}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => { if (!deleting) setConfirmOpen(false); }} maxWidth="xs" fullWidth>
                {deleting
                    ? <LinearProgress color="error" sx={{ height: 4 }} />
                    : <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>
                        Confirm Deletion
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">
                            {pendingDeleteIds.length}
                        </Typography>
                        {' '}source record(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                    <Button color="error" variant="contained" size="small" disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                        onClick={() => handleDelete(pendingDeleteIds)}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default SourceModule;
